// Pure competitive-rule helpers; CommonJS so plain `node` tests can require it.

// Single source of truth for the competitive rule name (UI side).
const COMPETITIVE_RULE_NAMES = ['competitive_adjustment']

function isCompetitiveRuleName(name) {
  return COMPETITIVE_RULE_NAMES.includes(name)
}

// Canonical competitor key: mirror extractBanners (BatchView.vue) replace(/\s/g,'_') so the engine can reverse the key.
function normalizeCompetitorKey(banner) {
  return String(banner).replace(/\s/g, '_')
}

function competitorKeyToLabel(key) {
  return String(key).replace(/_/g, ' ')
}

function hasCompetitiveRuleApplied(rulesApplied) {
  if (!rulesApplied) return false
  try {
    const rules = typeof rulesApplied === 'string' ? JSON.parse(rulesApplied) : rulesApplied
    return Array.isArray(rules) && rules.some(r => r && isCompetitiveRuleName(r.name))
  } catch (e) {
    return COMPETITIVE_RULE_NAMES.some(n => String(rulesApplied).includes(n))
  }
}

// Competitive add-on fields only; caller spreads onto base ruleEntry. Blank margin -> null, not a 0% floor.
// Owns the value: match direction nulls it, above/below coerce rule_value to a number.
function buildCompetitiveRuleEntry(selectedRule) {
  const r = selectedRule || {}
  const margin =
    r.margin_threshold === '' || r.margin_threshold == null ? null : Number(r.margin_threshold)
  const value = r.direction === 'match' ? null : Number(r.rule_value)
  return {
    competitor: r.competitor,
    direction: r.direction,
    value,
    apply_rounding: !!r.apply_rounding,
    margin_threshold: margin,
  }
}

// Signature fields; non-competitive rulesets get null/false defaults for backward-compat.
// direction + item_group are non-negotiable: omitting either falsely collapses differently-configured stores.
function competitiveSignatureFields(ruleset) {
  const r = ruleset || {}
  return {
    value: r.rule_value ?? null,
    competitor: r.competitor ?? null,
    direction: r.direction ?? null,
    item_group: r.item_group ?? null,
    margin_threshold: r.margin_threshold ?? null,
    apply_rounding: r.apply_rounding ?? false,
  }
}

// Save-gate validator: competitor + direction required; above/below also require a valid % value.
function competitiveRuleComplete(rule) {
  const r = rule || {}
  if (!r.competitor || !r.direction) return false
  if (r.direction === 'match') return true
  return indexPctRules().every(rule => rule(r.rule_value) === true)
}

// Batch View display gate: competitive v1 shows engine SRP; user edit (version_id>1) wins.
function selectDisplayPrice(item, isRetail, tprProTenants) {
  const competitive =
    item.competitive_rule_applied === true || hasCompetitiveRuleApplied(item.rules_applied)
  // store batches carry rules_promotion_price=0; trust it only when it's a real (>0) SRP
  if (competitive && item.version_id === 1 && Number(item.rules_promotion_price) > 0) {
    return item.rules_promotion_price
  }
  if (!isRetail && tprProTenants) {
    if (item.version_id > 1) return item.promotion_price
    if (item.version_id === 1 && item.rules_applied && Number(item.rules_promotion_price) > 0) return item.rules_promotion_price
    return item.promotion_price
  }
  return item.promotion_price
}

function maxTwoDecimals(value) {
  if (typeof value === 'string' && value.includes('.') && value.split('.')[1].length > 2) {
    return 'Only up to 2 decimal places allowed'
  }
  return true
}

// Index %: required, <=2 decimals, negatives allowed (no 0-100 clamp).
function indexPctRules() {
  return [
    value => (!value && value !== 0) || isNaN(value) ? 'Value must be a valid number' : true,
    maxTwoDecimals,
  ]
}

// Margin threshold: optional (blank/null passes), <=2 decimals, negatives allowed.
function marginThresholdRules() {
  return [
    value => value === '' || value == null || !isNaN(value) || 'Must be a valid number',
    maxTwoDecimals,
  ]
}

module.exports = {
  COMPETITIVE_RULE_NAMES,
  isCompetitiveRuleName,
  buildCompetitiveRuleEntry,
  selectDisplayPrice,
  competitiveSignatureFields,
  competitiveRuleComplete,
  normalizeCompetitorKey,
  competitorKeyToLabel,
  indexPctRules,
  marginThresholdRules,
  hasCompetitiveRuleApplied,
}
